import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { assetBookingShowsPeopleCount } from './assetBookingPeopleCount';

function coerceAssetTypeId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

/** Resolve `asset_type_name` from the picklist when only `asset_type_id` is set on the form. */
export function assetBodyWithResolvedAssetTypeName(
  assetBody: Record<string, unknown>,
  assetTypesCatalog: readonly GeneralInfoPicklistItem[],
): Record<string, unknown> {
  if (typeof assetBody.asset_type_name === 'string' && assetBody.asset_type_name.trim()) {
    return assetBody;
  }
  const typeId = coerceAssetTypeId(assetBody.asset_type_id);
  if (typeId === null) {
    return assetBody;
  }
  const match = assetTypesCatalog.find((item) => Number(item.id) === typeId);
  if (!match?.label?.trim()) {
    return assetBody;
  }
  return { ...assetBody, asset_type_name: match.label.trim() };
}

/** Backend requires `min_capacity` even when the create/edit UI only exposes max capacity. */
export const IMPLICIT_ASSET_MIN_CAPACITY = 1;

export function applyImplicitMinCapacityForAssetMutation(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const raw = body.min_capacity;
  const parsed =
    typeof raw === 'number' && Number.isFinite(raw)
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? Number(raw.trim())
        : NaN;
  if (Number.isFinite(parsed)) {
    return body;
  }
  return { ...body, min_capacity: IMPLICIT_ASSET_MIN_CAPACITY };
}

/** Open space, gym, parking, business lounge — show max capacity only. */
export function assetCreateShowsMaxCapacityOnly(
  assetBody: Record<string, unknown>,
  assetTypesCatalog: readonly GeneralInfoPicklistItem[],
): boolean {
  return assetBookingShowsPeopleCount(assetBodyWithResolvedAssetTypeName(assetBody, assetTypesCatalog));
}

export function applyAssetCreateCapacityFieldsToFormData(
  assetBody: Record<string, unknown>,
  assetTypesCatalog: readonly GeneralInfoPicklistItem[],
): Record<string, unknown> {
  const next = { ...assetBody };
  if (assetCreateShowsMaxCapacityOnly(next, assetTypesCatalog)) {
    delete next.min_capacity;
    return next;
  }
  delete next.min_capacity;
  delete next.max_capacity;
  return next;
}
